import React, { useEffect, useState } from 'react';
import { post, put, deleteRequest } from 'helpers/api';
import Modal from 'components/modal';
import Button from 'shared/button';
import ManagedInput from 'components/form/managed_input';
import WysiwygEditor from 'components/form/wysiwyg_editor';
import axios from 'axios';

type CustomCardModalProps = {
  projectId: number;
  cardId?: number;
  currentTitle?: string;
  currentSubtitle?: string;
  currentBody?: string;
  modalOpen: boolean;
  onCloseModal: () => void;
};

const NEW_CUSTOM_CARD_TITLE = 'Untitled Card';

export default function CustomCardModal(props: CustomCardModalProps) {
  const [cardId, setCardId] = useState(props.cardId);
  const [title, setTitle] = useState(props.currentTitle || '');
  const [subtitle, setSubtitle] = useState(props.currentSubtitle || '');
  const [body, setBody] = useState(props.currentBody || '');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showExitConfirm, setShowExitConfirm] = useState(false);
  const newCardUrl = `/projects/${props.projectId}/client_report/v2_cards`;
  const updateCardUrl = `/projects/${props.projectId}/client_report/v2_cards/${cardId}`;

  const customCardParams = {
    v2_card: {
      cardType: 'custom_card',
      displayCard: true,
      customCardAttributes: {
        title,
        subtitle,
        body
      }
    }
  };

  useEffect(() => {
    if (props.cardId) return;

    post(
      newCardUrl,
      {
        v2_card: {
          cardType: 'custom_card',
          displayCard: false,
          customCardAttributes: {
            title: NEW_CUSTOM_CARD_TITLE,
            body: ''
          }
        }
      }
    ).then((data: { clientReportV2Card: { id: number }}) => {
      setCardId(data.clientReportV2Card.id);
      setTitle(NEW_CUSTOM_CARD_TITLE);
    });
  }, []);

  const saveCustomCard = (e) => {
    e.preventDefault();
    put(updateCardUrl, customCardParams);
    closeModal();
  };

  const uploadImage = (image: File, config: any) => {
    if (!cardId) return;

    const formData = new FormData();
    formData.append('file', image);
    formData.append('email_ca', '1');

    return axios.post(
      `/projects/${props.projectId}/client_report/v2_cards/${cardId}/add_image`,
      formData,
      config
    );
  };

  const deleteCustomCard = (e) => {
    e.preventDefault();
    setShowDeleteConfirm(true);
  };

  const confirmDeleteCustomCard = () => {
    deleteRequest(updateCardUrl)
      .then(() => {
        closeModal();
      });
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
  };

  const cancelExit = () => {
    setShowExitConfirm(false);
  };

  const modalTitle = () => {
    if (showDeleteConfirm) {
      return 'Confirm Delete';
    }
    return 'Edit Custom Card';
  };

  const hasUnsavedChanges = () => {
    return (title !== (props.currentTitle || '') ||
    subtitle !== (props.currentSubtitle || '') ||
    body !== (props.currentBody || ''));
  };

  const handleCloseWithUnsavedCheck = () => {
    if (showDeleteConfirm || showExitConfirm) {
      return;
    } else if (hasUnsavedChanges()) {
      setShowExitConfirm(true);
    } else {
      closeModal();
    }
  };

  const closeModal = () => {
    props.onCloseModal();
  };

  const maxTitleLength = 50;

  const renderDeleteConfirm = () => {
    return (
      <div className="d-flex flex-column">
        <p>Are you sure you want to delete this custom card? This action cannot be undone.</p>
        <div className="d-flex justify-content-end">
          <Button.Secondary className="ds-button--plain mr-2" onClick={cancelDelete}>
            Cancel
          </Button.Secondary>
          <Button.Danger onClick={confirmDeleteCustomCard}>
            Yes, Delete
          </Button.Danger>
        </div>
      </div>
    );
  };

  const renderExitConfirm = () => {
    return (
      <div className="d-flex flex-column">
        <p>Are you sure you want to exit without saving changes? This action cannot be undone.</p>
        <div className="d-flex justify-content-end">
          <Button.Secondary className="ds-button--plain mr-2" onClick={cancelExit}>
            Cancel
          </Button.Secondary>
          <Button.Danger onClick={closeModal}>
            Yes, Exit
          </Button.Danger>
        </div>
      </div>
    );
  };

  const renderMainBody = () => {
    return (
      <>
        <div className={'form-group required'}>
          <label className="control-label text-capitalize">Title *</label>
          <div className="form-input-container">
            <ManagedInput
              className="form-control"
              maxLength={maxTitleLength}
              name="title"
              type="text"
              value={title}
              onChange={e => setTitle(e.title)}
            />
          </div>
        </div>
        <div className={'form-group'}>
          <label className="control-label text-capitalize">Subtitle</label>
          <div className="form-input-container">
            <ManagedInput
              className="form-control"
              maxLength={maxTitleLength}
              name="subtitle"
              type="text"
              value={subtitle}
              onChange={e => setSubtitle(e.subtitle)}
            />
          </div>
        </div>
        <div className="form-group required">
          <label className="control-label">Body *</label>
          <div className="form-input-container">
            <WysiwygEditor
              content={body}
              onChange={setBody}
              onUploadImage={uploadImage}
            />
          </div>
        </div>
      </>
    );
  };

  return (
    <Modal
      className="custom-card-modal"
      show={props.modalOpen}
      onHide={handleCloseWithUnsavedCheck}
    >
      <Modal.Header closeButton={!(showDeleteConfirm || showExitConfirm)}>
        {modalTitle()}
      </Modal.Header>
      <Modal.Body>
        {showDeleteConfirm
          ? renderDeleteConfirm()
          : showExitConfirm
            ? renderExitConfirm()
            : renderMainBody()
        }
      </Modal.Body>
      {(!showDeleteConfirm && !showExitConfirm) && (
        <Modal.Footer>
          <div className="d-flex justify-content-between w-100">
            <div>
              {cardId && (
                <Button.Danger className="ds-button--plain" style={{ cursor: 'pointer' }} onClick={deleteCustomCard}>
                  Delete
                </Button.Danger>
              )}
            </div>
            <div className="d-flex gap-2">
              <Button.Secondary
                className="ds-button--plain"
                style={{ cursor: 'pointer' }}
                onClick={handleCloseWithUnsavedCheck}
              >
                Cancel
              </Button.Secondary>
              <Button.Primary
                disabled={!cardId}
                style={{ cursor: 'pointer' }}
                type="submit"
                onClick={saveCustomCard}
              >
                Save
              </Button.Primary>
            </div>
          </div>
        </Modal.Footer>
      )}
    </Modal>
  );
}
